import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { TrendingUp, TrendingDown, Minus, ArrowUp, ArrowDown, ChevronDown, ChevronRight, BarChart3, X } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n || 0);

export default function PriceAnalytics() {
  const { user } = useAuth();
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMat, setSelectedMat] = useState(null);
  const [priceHistory, setPriceHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const canSeePrices = user?.role === 'owner' || user?.role === 'admin' || user?.permissions?.can_see_prices;

  useEffect(() => {
    if (!canSeePrices) return;
    const fetchTrends = async () => {
      try {
        const res = await axios.get(`${API}/inventory/price-trends`, { withCredentials: true });
        setTrends(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchTrends();
  }, [canSeePrices]);

  const loadPriceHistory = async (materialId) => {
    if (selectedMat === materialId) { setSelectedMat(null); setPriceHistory(null); return; }
    setHistoryLoading(true);
    setSelectedMat(materialId);
    try {
      const res = await axios.get(`${API}/inventory/price-history/${materialId}`, { withCredentials: true });
      setPriceHistory(res.data);
    } catch (e) { toast.error('Failed to load price history'); }
    finally { setHistoryLoading(false); }
  };

  if (!canSeePrices) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
        <BarChart3 size={32} className="text-zinc-700 mx-auto mb-3" />
        <p className="text-zinc-500 text-sm">Price analytics restricted to Admin / Owner</p>
      </div>
    );
  }

  if (loading) return <div className="text-zinc-400 text-sm animate-pulse">Loading price data...</div>;

  const upTrends = trends.filter(t => t.trend === 'up');
  const downTrends = trends.filter(t => t.trend === 'down');
  const stableTrends = trends.filter(t => t.trend === 'stable');

  return (
    <div className="space-y-4" data-testid="price-analytics">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-red-950/30 border border-red-800/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={16} className="text-red-400" />
            <span className="text-red-300 text-xs font-semibold">Price Increasing</span>
          </div>
          <div className="text-2xl font-heading font-bold text-red-300">{upTrends.length}</div>
          <p className="text-red-400/60 text-xs mt-0.5">materials with rising costs</p>
        </div>
        <div className="bg-green-950/30 border border-green-800/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown size={16} className="text-green-400" />
            <span className="text-green-300 text-xs font-semibold">Price Decreasing</span>
          </div>
          <div className="text-2xl font-heading font-bold text-green-300">{downTrends.length}</div>
          <p className="text-green-400/60 text-xs mt-0.5">materials with falling costs</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Minus size={16} className="text-zinc-400" />
            <span className="text-zinc-300 text-xs font-semibold">Stable</span>
          </div>
          <div className="text-2xl font-heading font-bold text-zinc-300">{stableTrends.length}</div>
          <p className="text-zinc-500 text-xs mt-0.5">materials with steady prices</p>
        </div>
      </div>

      {/* Price Trends Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-800/30">
          <h3 className="text-zinc-200 text-sm font-semibold">Raw Material Price Trends</h3>
          <p className="text-zinc-500 text-xs mt-0.5">Click any material to see detailed price chart</p>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-800">
            {['', 'Material', 'Latest Price', 'Avg Price', 'Min', 'Max', 'Change %', 'Trend', 'Purchases'].map(h => (
              <th key={h} className="text-left text-zinc-500 text-xs font-medium px-3 py-2.5">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {trends.map(t => (
              <React.Fragment key={t.material_id}>
                <tr className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer"
                  onClick={() => loadPriceHistory(t.material_id)}
                  data-testid={`price-row-${t.material_id}`}>
                  <td className="px-3 py-2.5 w-6">
                    {selectedMat === t.material_id
                      ? <ChevronDown size={12} className="text-amber-400" />
                      : <ChevronRight size={12} className="text-zinc-600" />}
                  </td>
                  <td className="px-3 py-2.5 text-zinc-200 text-xs font-medium">{t.material_name}</td>
                  <td className="px-3 py-2.5 font-mono text-zinc-200 text-xs font-semibold">{fmt(t.latest_price)}/{t.unit}</td>
                  <td className="px-3 py-2.5 font-mono text-zinc-400 text-xs">{fmt(t.avg_price)}</td>
                  <td className="px-3 py-2.5 font-mono text-green-400 text-xs">{fmt(t.min_price)}</td>
                  <td className="px-3 py-2.5 font-mono text-red-400 text-xs">{fmt(t.max_price)}</td>
                  <td className="px-3 py-2.5">
                    <span className={`font-mono text-xs font-semibold flex items-center gap-1 ${
                      t.pct_change > 5 ? 'text-red-400' : t.pct_change < -5 ? 'text-green-400' : 'text-zinc-400'
                    }`}>
                      {t.pct_change > 0 ? <ArrowUp size={10} /> : t.pct_change < 0 ? <ArrowDown size={10} /> : null}
                      {t.pct_change > 0 ? '+' : ''}{t.pct_change}%
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${
                      t.trend === 'up' ? 'bg-red-900/40 text-red-300 border-red-700' :
                      t.trend === 'down' ? 'bg-green-900/40 text-green-300 border-green-700' :
                      'bg-zinc-800 text-zinc-400 border-zinc-700'
                    }`}>
                      {t.trend === 'up' ? 'Rising' : t.trend === 'down' ? 'Falling' : 'Stable'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-zinc-500 text-xs">{t.purchase_count}</td>
                </tr>
                {/* Expanded price chart */}
                {selectedMat === t.material_id && priceHistory && !historyLoading && (
                  <tr><td colSpan={9} className="p-0">
                    <div className="bg-zinc-950/60 border-b border-zinc-800 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="text-zinc-200 text-sm font-semibold">{priceHistory.material_name} — Price History</h4>
                          <p className="text-zinc-500 text-xs">{priceHistory.stats.total_purchases} purchases | Stock: {priceHistory.current_stock} {priceHistory.unit}</p>
                        </div>
                        <div className="flex items-center gap-4 text-xs">
                          <div><span className="text-zinc-500">Avg: </span><span className="font-mono text-zinc-300">{fmt(priceHistory.stats.avg_price)}</span></div>
                          <div><span className="text-zinc-500">Min: </span><span className="font-mono text-green-400">{fmt(priceHistory.stats.min_price)}</span></div>
                          <div><span className="text-zinc-500">Max: </span><span className="font-mono text-red-400">{fmt(priceHistory.stats.max_price)}</span></div>
                          <div className={`font-mono font-semibold ${priceHistory.stats.pct_change > 0 ? 'text-red-400' : 'text-green-400'}`}>
                            {priceHistory.stats.pct_change > 0 ? '+' : ''}{priceHistory.stats.pct_change}%
                          </div>
                        </div>
                      </div>
                      {priceHistory.price_points.length > 1 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <AreaChart data={priceHistory.price_points}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                            <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} />
                            <YAxis tick={{ fill: '#71717a', fontSize: 10 }} domain={['auto', 'auto']} />
                            <Tooltip
                              contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, fontSize: 11 }}
                              labelStyle={{ color: '#a1a1aa' }}
                              formatter={(val) => [`${fmt(val)}`, 'Price']}
                            />
                            <Area type="monotone" dataKey="price" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.15} strokeWidth={2} />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-zinc-600 text-xs py-4 text-center">Not enough data points for chart (need 2+ purchases)</p>
                      )}
                      {/* Purchase details */}
                      <div className="mt-3">
                        <table className="w-full text-xs">
                          <thead><tr className="border-b border-zinc-800/60">
                            {['Date', 'Lot No', 'Rate', 'Qty', 'Vendor'].map(h => (
                              <th key={h} className="text-left text-zinc-500 text-[10px] font-medium px-2 py-1.5">{h}</th>
                            ))}
                          </tr></thead>
                          <tbody>
                            {priceHistory.price_points.map((p, i) => (
                              <tr key={i} className="border-b border-zinc-800/30">
                                <td className="px-2 py-1 font-mono text-zinc-400">{p.date}</td>
                                <td className="px-2 py-1"><span className="font-mono text-[10px] bg-amber-900/30 text-amber-300 border border-amber-700/40 px-1 py-0 rounded">{p.lot_no || '-'}</span></td>
                                <td className="px-2 py-1 font-mono text-zinc-200 font-semibold">{fmt(p.price)}</td>
                                <td className="px-2 py-1 font-mono text-zinc-400">{p.qty}</td>
                                <td className="px-2 py-1 text-zinc-500">{p.vendor || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {trends.length === 0 && <div className="text-center text-zinc-500 text-sm py-8">No price data available. Prices are recorded from lot purchases (feedbills, POs).</div>}
      </div>
    </div>
  );
}
